import {
  CaretakerRecommendationViewModel,
  CaretakerRecommendationDto,
  BroadcastService,
  BroadcastServiceDto,
  ResidenceRecommendationService,
  ResidenceRecommendationServiceDto,
} from './recommendation.model'
import { defaultTo } from 'lodash'
import { CareStepModel } from '$views/RecommendationsForms/RecommendationForm/CareStep/CareStep.model'
import * as dayjs from 'dayjs'
import 'dayjs/locale/es'
import { formatDateWithSlashes } from '$utils/dateFormatter'

const mapServiceDates = dates => {
  return dates.map(serviceDates => {
    return {
      startingDateTime: dayjs(serviceDates.startingDateTime).toDate(),
      finishingDateTime: dayjs(serviceDates.finishingDateTime).toDate(),
    }
  })
}

const isAdult = (careTypes: string[]) => careTypes.includes('adult')

const isChild = (careTypes: string[]) => careTypes.includes('child')

const hasExtra = (careTypes: string[]) => careTypes.includes('extra')

const mapRecommendations = (basicData: CaretakerRecommendationDto) => {
  if (basicData.availableCaretakers.length > 0) {
    return basicData.availableCaretakers.map(availableCaretaker => ({
      userId: defaultTo(availableCaretaker.userId, ''),
      caretakerId: defaultTo(availableCaretaker.caretakerId, ''),
      requestId: defaultTo(availableCaretaker.requestId, ''),
      name: defaultTo(availableCaretaker.name, ''),
      rating: defaultTo(availableCaretaker.rating, 0),
      price: defaultTo(availableCaretaker.price, 0),
      profilePicture: defaultTo(availableCaretaker.profilePicture, ''),
      caretakerHourlyRate: defaultTo(availableCaretaker.caretakerHourlyRate, ''),
      mainQualification: {
        qualificationName: defaultTo(availableCaretaker.qualificationName, ''),
        type: defaultTo(availableCaretaker.qualificationType, ''),
        specialty: defaultTo(availableCaretaker.qualificationSpecialty, ''),
        area: defaultTo(availableCaretaker.qualificationArea, ''),
      },
      presentationMessage: defaultTo(availableCaretaker.presentationMessage, ''),
      hasActiveProSubscription: defaultTo(availableCaretaker.hasActiveProSubscription, false),
      hasVerifiedBankData: defaultTo(availableCaretaker.hasVerifiedBankData, false),
      isVerified: defaultTo(availableCaretaker.isVerified, false),
      careType: {
        adult: isAdult(availableCaretaker.careType || []),
        child: isChild(availableCaretaker.careType || []),
        extra: hasExtra(availableCaretaker.careType || []),
      },
    }))
  }

  return []
}

export const basicDataResponseToModel = (basicData: CaretakerRecommendationDto): CaretakerRecommendationViewModel => {
  return {
    recommendations: mapRecommendations(basicData),
    service: {
      serviceDates: defaultTo(mapServiceDates(basicData.serviceDates), []),
      serviceLength: defaultTo(basicData.serviceLength, 0),
      location: defaultTo(basicData.serviceLocation, ''),
      caredType: defaultTo(basicData.caredType, 0),
      age: defaultTo(basicData.age, 0),
      isRecommendation: defaultTo(basicData.isRecommendation, false),
      nameOfSelectedCaretaker: defaultTo(basicData.nameOfSelectedCaretaker, ''),
      isClientSelectedCaretakerAvailable: defaultTo(basicData.isClientSelectedCaretakerAvailable, false),
      hiddenAvailableCaretakersAmount: defaultTo(basicData.hiddenAvailableCaretakersAmount, 0),
      hiddenPresentationMessagesAmount: defaultTo(basicData.hiddenPresentationMessagesAmount, 0),
      token: defaultTo(basicData.token, ''),
    },
  }
}

export const formatServiceMinutes = (minutes: number): string => {
  if (minutes < 10) {
    return `0${minutes}`
  }

  return `${minutes}`
}

export const formatServiceMonth = (startingService: Date) => startingService.toLocaleString('es-ES', { month: 'long' })

export const formatServiceHour = (startingService: Date, finishingService: Date) => {
  const startingMinutes = formatServiceMinutes(startingService.getMinutes())
  const finishingMinutes = formatServiceMinutes(finishingService.getMinutes())

  return `${startingService.getUTCHours()}:${startingMinutes}h - ${finishingService.getUTCHours()}:${finishingMinutes}h`
}

export const mapCaresToCaresModel = (cares: CareStepModel) => {
  const careModel = { companionship: 0, cleaness: 0, basics: 0, sanityAttention: 0, transport: 0 }
  if (cares.care.companionship) {
    careModel.companionship = 1
  }
  if (cares.care.cleaness) {
    careModel.cleaness = 1
  }
  if (cares.care.basics) {
    careModel.basics = 1
  }
  if (cares.care.sanityAttention) {
    careModel.sanityAttention = 1
  }
  if (cares.care.transport) {
    careModel.transport = 1
  }

  return careModel
}

export const mapBroadcastServiceToDtoModel = (service: BroadcastService): BroadcastServiceDto => {
  return {
    latitude: service.latitude ? service.latitude : 0,
    longitude: service.longitude ? service.longitude : 0,
    address: service.address ? service.address : '',
    province: service.province ? service.province : '',
    information: service.information ? service.information : '',
    phoneNumber: service.phoneNumber ? service.phoneNumber : '',
    careType: service.careType ? service.careType : '',
    hoursPerWeek: service.hoursPerWeek ? service.hoursPerWeek : '',
    name: service.name ? service.name : '',
    email: service.email ? service.email : '',
  }
}

const mapResidenceDates = serviceDates => {
  if (serviceDates) {
    return serviceDates.map(serviceDate => {
      serviceDate.date = formatDateWithSlashes(serviceDate.date)
      return serviceDate
    })
  }

  return undefined
}

export const mapResidenceRecommendationServiceToDtoModel = (
  service: ResidenceRecommendationService,
): ResidenceRecommendationServiceDto => {
  return {
    latitude: service.latitude ? service.latitude : 0,
    longitude: service.longitude ? service.longitude : 0,
    address: service.address ? service.address : '',
    province: service.province ? service.province : '',
    serviceDates: mapResidenceDates(service.serviceDates),
    residentId: service.residentId ? service.residentId : '',
    cares: {
      companionship: service.companionship ? 1 : 0,
      sanityAttention: service.sanityAttention ? 1 : 0,
      cleaness: service.cleaness ? 1 : 0,
      transport: service.transport ? 1 : 0,
    },
    extraInformation: service.extraInformation ? service.extraInformation : '',
    caretakerId: service.caretakerId ? service.caretakerId : '',
  }
}



// WEBPACK FOOTER //
// ./src/domain/model/recommendation/recommendation.mapper.ts